import { useRef, useEffect, useState, useCallback } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { getCotizacionesTabla, getNuevo } from '../../../service/AffariService';
import ClienteDialog from '../../clientes/components/dialogs/ClienteDialog';
import { validarRut, formatearRut } from '../../../utilities/RutUtil';

const DocumentoFilter = ({ 
    codigo, 
    setCodigo, 
    rut, 
    setRut, 
    razonSocial, 
    setRazonSocial, 
    onClear, 
    onNew,
    tipo,
    setTabla,
    setLoading = () => {}
}) => {
    const toast = useRef(null);
    const [visible, setVisible] = useState(false);
    const [dropdownData, setDropdownData] = useState(null);
    const onHide = () => setVisible(false);
    const lastToastTime = useRef(0);
    const [debouncing, setDebouncing] = useState({codigo: false,rut: false,razonSocial: false});
    const [limit, setLimit] = useState(50);
    const [order, setOrder] = useState("");
    const prevCodigo = useRef();
    const prevRut = useRef();
    const prevRazonSocial = useRef();

    const limitOptions = [
        { label: "50", value: 50 },
        { label: "100", value: 100 },
        { label: "150", value: 150 },
        { label: "Todos", value: 0 },
    ];

    const orderOptions = [
        { label: "Sin Orden", value: "" },
        { label: "Código", value: "cliente_codigo" },
        { label: "RUT", value: "cliente_rut" },
        { label: "Razón Social", value: "cliente_razon_social" },
    ];

    const showToast = useCallback((severity, summary, detail) => {
        const now = Date.now();
        if (now - lastToastTime.current > 500) {
            toast.current.show({ severity, summary, detail, life: 3000 });
            lastToastTime.current = now;
        }
    }, []);

    const handleSearch = useCallback(async (e, isAuto = false) => {

        if (e) e.preventDefault();

        if (rut && !validarRut(rut)) {
            if (!isAuto) showToast('error', 'Error', 'RUT inválido');
            return;
        }

        if (!rut && !codigo && !razonSocial) {
            if (!isAuto) showToast('warn', 'Advertencia', 'Debe ingresar por lo menos uno de los 3 filtros.');
            return;
        }

        if (razonSocial && razonSocial.length < 3) {
            if (!isAuto) showToast('warn', 'Advertencia', 'La Razón Social debe tener al menos 3 caracteres.');
            return;
        }
        
        setLoading(true);

        try {
            const data = await getCotizacionesTabla({
                tipo,
                rut,
                codigo,
                razonSocial,
                limit,
                order,
            });
            setTabla(data);

            if (Array.isArray(data) && data.length === 0) {
                if (!isAuto) showToast('info', 'Información', 'No se encontraron resultados');
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            if (!isAuto) showToast('error', 'Error', 'Ocurrió un error al obtener los datos.');
        } finally {
            setLoading(false);
        }
    }, [codigo, limit, order, razonSocial, rut, setLoading, setTabla, showToast, tipo]);

    useEffect(() => {
        if (!rut && !codigo && !razonSocial) return;
        if ((rut && !validarRut(rut)) || (razonSocial && razonSocial.length < 3)) return;
        handleSearch(null, true);
    }, [limit, order, codigo, rut, razonSocial, handleSearch]);

    useEffect(() => {
        const codigoChanged = codigo !== prevCodigo.current;
        const rutChanged = rut !== prevRut.current;
        const razonSocialChanged = razonSocial !== prevRazonSocial.current;

        prevCodigo.current = codigo;
        prevRut.current = rut;
        prevRazonSocial.current = razonSocial;

        if (!rut && !codigo && !razonSocial) {
            setDebouncing({ codigo: false, rut: false, razonSocial: false });
            return;
        }

        if (codigoChanged || rutChanged || razonSocialChanged) {
            setDebouncing(prev => ({
                codigo: codigoChanged ? true : prev.codigo,
                rut: rutChanged ? true : prev.rut,
                razonSocial: razonSocialChanged ? true : prev.razonSocial
            }));
        }

        const timer = setTimeout(() => {
            setDebouncing({ codigo: false, rut: false, razonSocial: false });
            if ((rut && !validarRut(rut)) || (razonSocial && razonSocial.length < 3) || (!rut && !codigo && !razonSocial)) {
                return;
            }
            handleSearch(null, true);
        }, 1000);

        return () => clearTimeout(timer);
    }, [codigo, rut, razonSocial, handleSearch]);

    const handleRutBlur = () => {
        if (rut && !validarRut(rut)) {
            showToast('error', 'Error', 'RUT inválido');
        }
    };

    return (
        <>
            <form onSubmit={handleSearch}>
                <Toast ref={toast} />
                <div className="flex flex-column md:flex-row gap-3 md:align-items-end md:justify-content-between p-fluid">
                    <div className="flex flex-column md:flex-row gap-3 flex-grow-1">
                        <div className="flex flex-column md:flex-row gap-3 flex-grow-1 md:justify-content-start md:align-items-end">
                            {/* codigo */}
                            <div className="flex-grow-2">
                                <label htmlFor="codigo" className="font-medium text-600 mb-1 block">Código</label>
                                <span className="p-input-icon-left p-input-icon-right w-full">
                                    <i className="pi pi-hashtag" />
                                    <InputText
                                        id="codigo"
                                        value={codigo}
                                        onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ''))}
                                        placeholder="Código"
                                        maxLength={35}
                                        className="w-full p-inputtext-sm"
                                    />
                                    <i className="pi pi-spin pi-spinner" style={{ opacity: debouncing.codigo ? 1 : 0 }} />
                                </span>
                            </div>

                            {/* RUT */}
                            <div className="flex-grow-2">
                                <label htmlFor="rut" className="font-medium text-600 mb-1 block">RUT</label>
                                <span className="p-input-icon-left p-input-icon-right w-full">
                                    <i className="pi pi-id-card" />
                                    <InputText
                                        id="rut"
                                        value={rut}
                                        onChange={(e) => setRut(formatearRut(e.target.value))}
                                        onBlur={handleRutBlur}
                                        placeholder="RUT"
                                        maxLength={12}
                                        className={`w-full p-inputtext-sm ${rut && !validarRut(rut) ? 'p-invalid' : ''}`}
                                    />
                                    <i className="pi pi-spin pi-spinner" style={{ opacity: debouncing.rut ? 1 : 0 }} />
                                </span>
                            </div>

                            {/* Razón Social */}
                            <div className="flex-grow-2">
                                <label htmlFor="razonSocial" className="font-medium text-600 mb-1 block">Razón Social</label>
                                <span className="p-input-icon-left p-input-icon-right w-full">
                                    <i className="pi pi-building" />
                                    <InputText
                                        id="razonSocial"
                                        value={razonSocial}
                                        onChange={(e) => setRazonSocial(e.target.value)}
                                        placeholder="Razón Social"
                                        className={`w-full p-inputtext-sm ${razonSocial && razonSocial.length < 3 ? 'p-invalid' : ''}`}
                                    />
                                    <i className="pi pi-spin pi-spinner" style={{ opacity: debouncing.razonSocial ? 1 : 0 }} />
                                </span>
                            </div>

                            <div className="flex-grow-2">
                                <label htmlFor="limit" className="font-medium text-600 mb-1 block">Mostrar</label>
                                <Dropdown
                                    id="limit"
                                    value={limit}
                                    options={limitOptions}
                                    optionLabel="label"
                                    optionValue="value"
                                    onChange={(e) => setLimit(e.value)}
                                    className="w-full p-inputtext-sm"
                                />
                            </div>

                            <div className="flex-grow-2">
                                <label htmlFor="order" className="font-medium text-600 mb-1 block">Ordenar por</label>
                                <Dropdown
                                    id="order"
                                    value={order}
                                    options={orderOptions}
                                    optionLabel="label"
                                    optionValue="value"
                                    onChange={(e) => setOrder(e.value)}
                                    className="w-full p-inputtext-sm"
                                />
                            </div>
                            
                            <div className="flex  md:flex-row gap-2 md:justify-content-end">
                                <Button
                                    type="submit"
                                    icon="pi pi-search"
                                    className="p-button-primary p-button-md"
                                    tooltip="Buscar"
                                    label='Buscar'
                                />
                                <Button
                                    type="button"
                                    className="p-button-secondary p-button-md"
                                    icon="pi pi-eraser"
                                    onClick={() => {
                                        setLimit(50);
                                        setOrder("");
                                        onClear();
                                    }}
                                    tooltip="Limpiar"
                                    label='Limpiar'
                                />
                                
                            </div>
                        </div>
                    </div>

                    {/* Código */}
                    {tipo === "nota-venta" ? null : (
                        <div>
                            <Button
                                type="button"
                                className="p-button-success"
                                icon="pi pi-plus"
                                label="Nuevo"
                                onClick={() => {
                                    getNuevo().then((data) => {
                                    setDropdownData(data);
                                    setVisible(true);
                                    onNew();
                                    }).catch(error => {
                                        console.error("Error fetching new client data:", error);
                                        showToast('error', 'Error', 'No se pudieron cargar los datos del formulario.');
                                    });
                                }}
                            />
                        </div>
                    )}
                </div>
            </form>

            <ClienteDialog
                visible={visible}
                onHide={onHide}
                dropdownData={dropdownData}
            />
        </>
    );
};

export default DocumentoFilter;